import { FeedObjectFocusName } from "class/Feed";
import { useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Focus, FocusObject } from "class/Focus";
import { State } from "store/types";

import { setReportFormDisabled, setReportSettings as SetReportSettings } from "store/article/report/reducers";
import { ReportDefinitionSettings } from "store/models/reportConfiguration";

import { isEmpty, uniq } from "lodash";
import { InlineNotification } from "@launchmetrics/lm-ui";
import { useTranslation } from "react-i18next";


export const useFeedWarnings = () => {

	const { t } = useTranslation();

	const dispatch = useDispatch();
	const filterFeedIds = useSelector((state: State) => state.search.filters.feeds);
	const allFocus = useSelector((state: State) => state.focus.list.focusList);
	const isAllFeedsSelected = filterFeedIds.length === 1 && filterFeedIds[0] === 'all';

	const getFocusIds = useCallback((feedIds: string[]): string[] => {
		const focusIds = feedIds
			.map((feedId: string) => {
				const focus = Focus.findFeedInFocusList(feedId, allFocus as FocusObject[]);
				return focus ? focus.focus_id : null;
			})
			.filter((focusId): focusId is string => !!focusId);
		return uniq(focusIds);
	}, [allFocus]); 

	const focusIds = useMemo(() => getFocusIds(filterFeedIds), [getFocusIds, filterFeedIds]);
	const warning = useMemo(() => {
		if (isEmpty(filterFeedIds) || isAllFeedsSelected) {
			return <InlineNotification id={'report-no-feed-warning'} message={t('reports.side_panel.warnings.no_feeds')} type="warning" />;
		}
		if (focusIds.length > 1) {
			return <InlineNotification id={'report-more-than-one-feed-warning'} message={t('reports.side_panel.warnings.multiple_focus')} type="warning" />;
		}
		return null;
	}, [filterFeedIds, focusIds.length, t, isAllFeedsSelected]);

	dispatch(setReportFormDisabled(!!warning));

	useEffect(() => {
		dispatch(setReportFormDisabled(!!warning));
	}, [dispatch, warning]);

	return warning;
};

export function useReportSettings(): [ReportDefinitionSettings, (settings: Partial<ReportDefinitionSettings>) => void] {
	const dispatch = useDispatch();
	const reportSettings = useSelector((state: State) => state.article.report.configuration.definition.settings);

	const setReportSettings = useCallback((settings: Partial<ReportDefinitionSettings>) => {
		dispatch(SetReportSettings(settings));
	}, [dispatch]);

	return [
		reportSettings,
		setReportSettings
	];
}

export function useFeedFinder(feedIds: string[]): FeedObjectFocusName[] {
	const focusList = useSelector((state: State) => state.focus.list.focusList);
	const feeds = useMemo(() =>
		feedIds.map((feedId: string) => Focus.findFeedInFocusList(feedId, focusList || [])).filter(feed => feed !== null) as FeedObjectFocusName[]
	, [feedIds, focusList]);

	return feeds;
}
